import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle2, Building2, Globe,
  Upload, Settings, Target, Play, Rocket, Loader2
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'followup', label: 'Follow up unclosed estimates', prompt: 'Create a workflow to automatically follow up on unclosed estimates via email and phone after 48 hours' },
  { id: 'receptionist', label: 'AI Receptionist', prompt: 'Create an AI receptionist that answers inbound calls, qualifies leads, and books appointments' },
  { id: 'scheduling', label: 'Production Scheduling', prompt: 'Create a production scheduling workflow that reads the CRM calendar, avoids conflicts, and updates job stages' },
  { id: 'estimating', label: 'Estimating & Supplements', prompt: 'Create an estimating workflow that generates estimates from measurements and writes insurance supplements' },
  { id: 'custom', label: 'Custom Goal', prompt: '' },
];

const INDUSTRIES = ['general', 'construction', 'healthcare', 'insurance', 'realestate', 'legal', 'ecommerce', 'marketing', 'accounting', 'logistics'];

const BusinessOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bizId, setBizId] = useState(null);
  const [bpId, setBpId] = useState(null);

  // Step 1
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [industry, setIndustry] = useState('general');

  // Step 2
  const [notes, setNotes] = useState('');
  const [sopContent, setSopContent] = useState('');
  const [sopName, setSopName] = useState('');

  // Step 4
  const [selectedGoal, setSelectedGoal] = useState('');
  const [customGoal, setCustomGoal] = useState('');

  // Step 5
  const [blueprint, setBlueprint] = useState(null);

  const handleStep1 = async () => {
    if (!name) return toast.error('Business name required');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/businesses`, { name, website_url: url, industry });
      setBizId(res.data.id);
      await axios.patch(`${API}/businesses/${res.data.id}`, { onboarding_status: 'in_progress' });
      // Summarize website if URL provided
      if (url) {
        try { await axios.post(`${API}/businesses/${res.data.id}/summarize-website`); } catch {}
      }
      setStep(1);
    } catch (err) { toast.error('Failed to create business'); }
    finally { setLoading(false); }
  };

  const handleStep2 = async () => {
    setLoading(true);
    try {
      if (notes) await axios.patch(`${API}/knowledge/${bizId}`, { onboarding_notes: notes });
      if (sopContent && sopName) {
        await axios.post(`${API}/knowledge/files/upload`, { business_id: bizId, filename: sopName, content: sopContent, source_type: 'sop' });
      }
      setStep(2);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleStep4 = async () => {
    if (!selectedGoal && !customGoal) return toast.error('Select or enter a goal');
    setLoading(true);
    try {
      const goal = GOALS.find(g => g.id === selectedGoal);
      const prompt = goal?.id === 'custom' ? customGoal : (goal?.prompt || customGoal);

      const res = await axios.post(`${API}/operator/command`, {
        mode: 'create',
        message: prompt,
        context: { business_id: bizId },
      });
      setBpId(res.data.blueprint_id);
      setBlueprint(res.data);
      setStep(4);
    } catch { toast.error('Workflow generation failed'); }
    finally { setLoading(false); }
  };

  const handleDeploy = async () => {
    if (!bpId) return;
    setLoading(true);
    try {
      await axios.post(`${API}/operator/command`, { mode: 'deploy', message: '', context: { blueprint_id: bpId } });
      await axios.patch(`${API}/businesses/${bizId}`, { onboarding_status: 'complete' });
      toast.success('First workflow deployed!');
      setStep(5);
    } catch { toast.error('Deploy failed'); }
    finally { setLoading(false); }
  };

  const STEPS = ['Company', 'Knowledge', 'Integrations', 'Goal', 'Workflow', 'Complete'];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <div className="noise-overlay" />
      <nav className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2"><HSLogo size={28} /><span className="font-black text-base">HumanScaled</span></div>
          <span className="text-xs text-muted-foreground font-mono">BUSINESS SETUP</span>
        </div>
      </nav>

      {/* Step Indicator */}
      <div className="max-w-3xl mx-auto px-6 pt-8 pb-4 w-full">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className={`flex-1 h-0.5 ${step >= i ? 'bg-emerald-500' : 'bg-white/10'}`} />}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-mono ${step >= i ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-muted-foreground'}`}>
                  {step > i ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className="text-[9px] mt-1 hidden sm:block text-muted-foreground">{s}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="flex-1 max-w-3xl mx-auto px-6 py-8 w-full">
        {/* STEP 1: Company Info */}
        {step === 0 && (
          <div className="space-y-6" data-testid="step-company">
            <div><h2 className="text-2xl font-bold mb-1">Set Up Your Business</h2><p className="text-muted-foreground">Tell us about the company you are building workflows for.</p></div>
            <div className="space-y-4">
              <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Business Name *</label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Premier Roofing Co" className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="onboard-name" /></div>
              <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Website URL</label><Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://company.com" className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="onboard-url" /></div>
              <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Industry</label>
                <Select value={industry} onValueChange={setIndustry}><SelectTrigger className="bg-[#0a0a0a] border-white/10 rounded-sm"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0a0a0a] border-white/10">{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <Button onClick={handleStep1} disabled={loading} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="onboard-next-1">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}</Button>
          </div>
        )}

        {/* STEP 2: Knowledge */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-knowledge">
            <div><h2 className="text-2xl font-bold mb-1">Add Knowledge</h2><p className="text-muted-foreground">Help your AI understand the business. All optional — you can add more later.</p></div>
            <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Onboarding Notes</label><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Key details: business preferences, special requirements, key contacts..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[80px]" /></div>
            <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Upload SOP (optional)</label><Input value={sopName} onChange={e => setSopName(e.target.value)} placeholder="sop-name.txt" className="bg-[#0a0a0a] border-white/10 rounded-sm mb-2" /><Textarea value={sopContent} onChange={e => setSopContent(e.target.value)} placeholder="Paste SOP content..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[60px]" /></div>
            <div className="flex gap-3">
              <Button onClick={() => setStep(0)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleStep2} disabled={loading} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="onboard-next-2">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}</Button>
            </div>
          </div>
        )}

        {/* STEP 3: Integrations */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-integrations">
            <div><h2 className="text-2xl font-bold mb-1">Connect Integrations</h2><p className="text-muted-foreground">Connect your tools. You can configure these later.</p></div>
            <div className="space-y-3">
              {[{ name: 'CRM', desc: 'HubSpot, Salesforce, or Internal', path: '/crm-settings' }, { name: 'Email', desc: 'Resend (configured)', status: 'Connected' }, { name: 'Voice AI', desc: 'ElevenLabs (configured)', status: 'Connected' }].map((int, i) => (
                <div key={i} className="bg-[#0a0a0a] border border-white/5 p-4 flex items-center justify-between">
                  <div><p className="font-medium text-sm">{int.name}</p><p className="text-xs text-muted-foreground">{int.desc}</p></div>
                  {int.status ? <span className="text-xs text-emerald-400 font-mono">{int.status}</span> : <Button onClick={() => navigate(int.path)} variant="outline" size="sm" className="border-white/10 rounded-sm h-7 text-xs">Configure</Button>}
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="onboard-next-3">Continue <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </div>
          </div>
        )}

        {/* STEP 4: Choose Goal */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-goal">
            <div><h2 className="text-2xl font-bold mb-1">Choose Your First Goal</h2><p className="text-muted-foreground">What should your first AI workflow do?</p></div>
            <div className="space-y-2">
              {GOALS.map(g => (
                <button key={g.id} onClick={() => setSelectedGoal(g.id)} className={`w-full text-left p-4 border transition-all ${selectedGoal === g.id ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/5 hover:border-white/10'}`} data-testid={`goal-${g.id}`}>
                  <p className="font-medium text-sm">{g.label}</p>
                  {g.prompt && <p className="text-xs text-muted-foreground mt-0.5">{g.prompt.slice(0, 60)}...</p>}
                </button>
              ))}
            </div>
            {selectedGoal === 'custom' && <Textarea value={customGoal} onChange={e => setCustomGoal(e.target.value)} placeholder="Describe your workflow goal..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[60px]" data-testid="custom-goal" />}
            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleStep4} disabled={loading || (!selectedGoal && !customGoal)} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="onboard-generate">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <><Rocket className="w-4 h-4 mr-2" /></>} Generate Workflow</Button>
            </div>
          </div>
        )}

        {/* STEP 5: Review Workflow */}
        {step === 4 && blueprint && (
          <div className="space-y-6" data-testid="step-workflow">
            <div><h2 className="text-2xl font-bold mb-1">Your Workflow is Ready</h2><p className="text-muted-foreground">{blueprint.assistant_message}</p></div>
            <div className="bg-[#0a0a0a] border border-emerald-500/20 p-5">
              <h3 className="font-bold mb-2">{blueprint.blueprint_name}</h3>
              {blueprint.validation_report && (
                <div className="flex items-center gap-1.5 mb-3">
                  {blueprint.validation_report.valid ? <><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-xs text-emerald-400">Valid and ready</span></> : <span className="text-xs text-amber-400">Has warnings</span>}
                </div>
              )}
              <div className="flex gap-3">
                <Button onClick={() => navigate(`/workflow`)} variant="outline" className="border-white/10 rounded-sm">Open in Builder</Button>
                <Button onClick={handleDeploy} disabled={loading} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-11" data-testid="onboard-deploy">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-2" /> Deploy (Shadow Mode)</>}</Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: Complete */}
        {step === 5 && (
          <div className="text-center space-y-6 py-12" data-testid="step-complete">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-emerald-400" /></div>
            <h2 className="text-2xl font-bold">Setup Complete</h2>
            <p className="text-muted-foreground">Your first workflow is deployed. Monitor it from the Agent Monitor.</p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => navigate('/agents')} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm">View Agents</Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-white/10 rounded-sm">Go to Dashboard</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessOnboarding;
